import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import type { PeopleSource } from "@playbookai/types";
import type { RemoteFilterOptions } from "@playbookai/ui";
import { kpiApi } from "../api/live-client.js";
import { kpiAuthScopeKey } from "../hooks/kpiAuthScope.js";
import { kpiKeys } from "../hooks/kpiQueryKeys.js";
import { personNamesById, type KpiPersonName } from "../hooks/useOwnerNames.js";

/**
 * The Owner filter's remote option source (PB-2941).
 *
 * All KPIs is cursor-paginated, so owner choices must cover every distinct
 * owner in the tenant — not just owners visible on loaded KPI pages. The KPI
 * backend serves the distinct owner IDS (`GET /v1/kpis/owner-options`); their
 * display NAMES belong to Auth/RBAC and resolve through the host's
 * `PeopleSource.resolve` — the same resolve and `kpiKeys.ownerNames` cache
 * family the list's OwnerChips use, so nothing is duplicated. Each option is a
 * FilterMenu person row (`person: {}` — a resolved person carries no email),
 * so it draws the owner's avatar beside the name.
 *
 * Name SEARCH cannot run server-side (playbook-be holds no names), so the
 * loader assembles the complete distinct-owner set once — a bounded cursor
 * sweep, cached and in-flight-deduplicated by TanStack (`fetchQuery`) — then
 * resolves names in one batched call and searches/paginates that assembled set
 * client-side. Search therefore covers EVERY owner, never a partial page; if
 * the sweep would exceed its bound, the loader throws (surfaced as the
 * option list's error + retry state) rather than silently searching a subset.
 *
 * Cache identity: the shell's QueryClient is created once and SURVIVES
 * in-page auth transitions, so BOTH identity-bearing keys — the swept ids and
 * the resolved names — embed the host auth scope (`kpiAuthScopeKey`, the
 * SOLVE/Hoshin scope-keyed-cache pattern). One scope key is captured per load
 * operation, before the first await, so the id sweep and the name resolution
 * of a single load always key under the same identity.
 */
const OWNER_SWEEP_PAGE_LIMIT = 100;
const MAX_OWNER_SWEEP_PAGES = 25;
const OWNER_OPTION_PAGE_SIZE = 20;
const OWNER_IDS_STALE_MS = 60_000;
const OWNER_NAMES_STALE_MS = 5 * 60_000;

// Mirrors the backend's UUID-shape guard: an owner value restored from the
// URL is dropped unless it could be a real id (fail-safe, never a 400).
const UUID_SHAPE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Truthful fallback when the people source does not know an id. */
export function ownerIdFallbackLabel(ownerUserId: string): string {
  return `${ownerUserId.slice(0, 8)}…`;
}

export function useOwnerFilterOptions(
  source: PeopleSource | null,
): RemoteFilterOptions {
  const config = useApiClient();
  const queryClient = useQueryClient();

  return useMemo<RemoteFilterOptions>(() => {
    const assertScopeCurrent = (scopeKey: string): void => {
      const currentScopeKey = kpiAuthScopeKey(
        config.authScope?.getSnapshot() ?? null,
      );
      if (currentScopeKey !== scopeKey) {
        throw new DOMException(
          "KPI owner options request crossed an auth-scope change.",
          "AbortError",
        );
      }
    };

    const loadAllOwnerIds = (scopeKey: string): Promise<string[]> =>
      queryClient.fetchQuery({
        queryKey: kpiKeys.ownerOptionIds(scopeKey),
        queryFn: async () => {
          const ids: string[] = [];
          let cursor: string | undefined;
          for (let page = 0; page < MAX_OWNER_SWEEP_PAGES; page++) {
            const res = await kpiApi.listOwnerOptions(config, {
              limit: OWNER_SWEEP_PAGE_LIMIT,
              ...(cursor ? { cursor } : {}),
            });
            assertScopeCurrent(scopeKey);
            ids.push(...res.data.map((option) => option.ownerUserId));
            if (!res.hasMore) return ids;
            if (!res.nextCursor) {
              throw new Error(
                "KPI owner options indicated more results without a next cursor.",
              );
            }
            cursor = res.nextCursor;
          }
          throw new Error(
            `KPI owner options exceeded ${MAX_OWNER_SWEEP_PAGES} pages; refusing to search a partial owner set.`,
          );
        },
        staleTime: OWNER_IDS_STALE_MS,
      });

    const resolveOwnerNames = (
      scopeKey: string,
      ids: readonly string[],
    ): Promise<Record<string, KpiPersonName>> => {
      // Nothing to ask: every owner keeps its id fallback, and nothing is
      // cached under the names key.
      if (!source?.resolve) return Promise.resolve({});
      // Sorted ids, matching useOwnerNames' key discipline — identical row
      // sets share one cache entry regardless of arrival order.
      const sorted = [...ids].sort();
      return queryClient.fetchQuery({
        queryKey: kpiKeys.ownerNames(scopeKey, sorted),
        queryFn: async () => {
          const names = personNamesById(await source.resolve!(sorted));
          assertScopeCurrent(scopeKey);
          return names;
        },
        staleTime: OWNER_NAMES_STALE_MS,
      });
    };

    return {
      kind: "remote",
      searchable: true,
      searchPlaceholder: "Search owners…",
      emptyMessage: "No owners found.",
      isValidValue: (value) => UUID_SHAPE.test(value),
      load: async ({ query, cursor }) => {
        // ONE scope key per load operation, captured before the first await:
        // the id sweep and the name resolution must key under the SAME
        // identity, so an auth change between the two awaits can never pair
        // tenant A's ids with tenant B's names.
        const scopeKey = kpiAuthScopeKey(
          config.authScope?.getSnapshot() ?? null,
        );
        const ids = await loadAllOwnerIds(scopeKey);
        assertScopeCurrent(scopeKey);
        if (ids.length === 0) return { options: [], nextCursor: null };
        const names = await resolveOwnerNames(scopeKey, ids);
        assertScopeCurrent(scopeKey);
        const needle = query.trim().toLocaleLowerCase();
        // Distinct by stable user ID (two users may share a display name —
        // they stay separate options); an unknown id renders truthfully.
        const matched = ids
          .map((id) => ({
            value: id,
            label: names[id]?.name ?? ownerIdFallbackLabel(id),
            person: {},
          }))
          .filter(
            (option) =>
              needle === "" ||
              option.label.toLocaleLowerCase().includes(needle),
          )
          .sort((a, b) => a.label.localeCompare(b.label));
        const offset = cursor
          ? Math.max(0, Number.parseInt(cursor, 10) || 0)
          : 0;
        const page = matched.slice(offset, offset + OWNER_OPTION_PAGE_SIZE);
        const nextOffset = offset + OWNER_OPTION_PAGE_SIZE;
        return {
          options: page,
          nextCursor: nextOffset < matched.length ? String(nextOffset) : null,
        };
      },
    };
  }, [config, source, queryClient]);
}
